import { createTenantClient } from './apiClient';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = createTenantClient(API_BASE_URL);

// ========== Frameworks ==========

export const getFrameworks = async (params = {}) => {
  const response = await api.get('/api/grc/frameworks', { params });
  return response.data;
};

export const getFramework = async (frameworkId) => {
  const response = await api.get(`/api/grc/frameworks/${frameworkId}`);
  return response.data;
};

export const createFramework = async (data) => {
  const response = await api.post('/api/grc/frameworks', data);
  return response.data;
};

export const getAvailableFrameworks = async () => {
  const response = await api.get('/api/grc/available-frameworks');
  return response.data;
};

export const enableFrameworkWithControls = async (code, owner = "Security Team") => {
  const response = await api.post(`/api/grc/frameworks/enable/${code}`, null, {
    params: { owner }
  });
  return response.data;
};

// ========== Tenant Users ==========

export const getTenantUsers = async () => {
  const response = await api.get('/api/grc/tenant-users');
  return response.data;
};

export const disableFramework = async (code, deleteControls = false) => {
  const response = await api.delete(`/api/grc/frameworks/disable/${code}`, {
    params: { delete_controls: deleteControls }
  });
  return response.data;
};

export const syncFrameworkControls = async (code, owner = "Security Team") => {
  const response = await api.post(`/api/grc/frameworks/sync/${code}`, null, {
    params: { owner }
  });
  return response.data;
};

export const enableFramework = async (tenantId, frameworkId) => {
  const response = await api.post(`/api/grc/tenants/${tenantId}/frameworks/${frameworkId}`);
  return response.data;
};

export const getTenantFrameworks = async (tenantId) => {
  const response = await api.get(`/api/grc/tenants/${tenantId}/frameworks`);
  return response.data;
};

// Get frameworks for current user's tenant (no tenantId required)
export const getCurrentTenantFrameworks = async () => {
  const response = await api.get('/api/grc/tenant-frameworks');
  return response.data;
};

// ========== Controls ==========

export const getControls = async (params = {}) => {
  const response = await api.get('/api/grc/controls', { params });
  return response.data;
};

export const getControl = async (controlId) => {
  const response = await api.get(`/api/grc/controls/${controlId}`);
  return response.data;
};

export const createControl = async (data) => {
  const response = await api.post('/api/grc/controls', data);
  return response.data;
};

export const updateControl = async (controlId, data) => {
  const response = await api.put(`/api/grc/controls/${controlId}`, data);
  return response.data;
};

export const deleteControl = async (controlId) => {
  const response = await api.delete(`/api/grc/controls/${controlId}`);
  return response.data;
};

// ========== Documents ==========

export const uploadDocument = async (formData) => {
  const response = await api.post('/api/grc/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const attachDocumentFile = async (documentId, formData) => {
  const response = await api.post(`/api/grc/documents/${documentId}/attach`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const getDocuments = async (params = {}) => {
  const response = await api.get('/api/grc/documents', { params });
  return response.data;
};

export const getDocument = async (documentId) => {
  const response = await api.get(`/api/grc/documents/${documentId}`);
  return response.data;
};

export const downloadDocument = async (documentId, filename) => {
  const response = await api.get(`/api/grc/documents/${documentId}/download`, {
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const approveDocument = async (documentId, action, comments) => {
  const response = await api.post(`/api/grc/documents/${documentId}/approve`, {
    action,
    comments
  });
  return response.data;
};

export const updateDocument = async (documentId, data) => {
  const response = await api.put(`/api/grc/documents/${documentId}`, data);
  return response.data;
};

export const deleteDocument = async (documentId) => {
  const response = await api.delete(`/api/grc/documents/${documentId}`);
  return response.data;
};

// ========== Evidence ==========

export const uploadEvidence = async (formData) => {
  const response = await api.post('/api/grc/evidence/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const attachEvidenceFile = async (evidenceId, formData) => {
  const response = await api.post(`/api/grc/evidence/${evidenceId}/attach`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const getEvidence = async (params = {}) => {
  const response = await api.get('/api/grc/evidence', { params });
  return response.data;
};

export const getEvidenceById = async (evidenceId) => {
  const response = await api.get(`/api/grc/evidence/${evidenceId}`);
  return response.data;
};

export const downloadEvidence = async (evidenceId, filename) => {
  const response = await api.get(`/api/grc/evidence/${evidenceId}/download`, {
    responseType: 'blob'
  });
  
  // Determine filename: backend may serve JSON for auto-evidence; respect ext
  const cd = response.headers?.['content-disposition'] || '';
  const m = /filename="?([^"]+)"?/.exec(cd);
  const fname = (m && m[1]) || filename || 'evidence';
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fname);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const previewEvidence = async (evidenceId) => {
  const response = await api.get(`/api/grc/evidence/${evidenceId}/preview`);
  return response.data;
};

export const fetchEvidenceInlineBlob = async (evidenceId) => {
  const response = await api.get(`/api/grc/evidence/${evidenceId}/inline`, {
    responseType: 'blob'
  });
  return {
    blob: response.data,
    mime: response.headers?.['content-type'] || 'application/octet-stream',
  };
};

export const approveEvidence = async (evidenceId, action, comments) => {
  const response = await api.post(`/api/grc/evidence/${evidenceId}/approve`, {
    action,
    comments
  });
  return response.data;
};

export const updateEvidence = async (evidenceId, data) => {
  const response = await api.put(`/api/grc/evidence/${evidenceId}`, data);
  return response.data;
};

export const deleteEvidence = async (evidenceId) => {
  const response = await api.delete(`/api/grc/evidence/${evidenceId}`);
  return response.data;
};

// ========== Approval History ==========

export const getApprovalHistory = async (resourceType, resourceId) => {
  const response = await api.get(`/api/grc/approval-history/${resourceType}/${resourceId}`);
  return response.data;
};

// ========== Policy Approval ==========

export const approvePolicy = async (policyId, action, comments) => {
  const response = await api.post(`/api/grc/policies/${policyId}/approve`, {
    action,
    comments
  });
  return response.data;
};

// ========== Dashboard & Analytics ==========

export const getExecutiveDashboard = async () => {
  const response = await api.get('/api/grc/dashboard/executive');
  return response.data;
};

export const getMappingReport = async (frameworkId) => {
  const response = await api.get(`/api/grc/mapping-report/${frameworkId}`);
  return response.data;
};

// ========== Compliance Trends ==========

export const getComplianceTrends = async (days = 90) => {
  const response = await api.get('/api/grc/trends/historical', { params: { days } });
  return response.data;
};

export const recordComplianceSnapshot = async () => {
  const response = await api.post('/api/grc/trends/snapshot');
  return response.data;
};

// ========== Custom Frameworks ==========

export const getFrameworkTemplates = async () => {
  const response = await api.get('/api/grc/custom-frameworks/templates');
  return response.data;
};

export const getCustomFrameworks = async () => {
  const response = await api.get('/api/grc/custom-frameworks');
  return response.data;
};

export const createCustomFramework = async (data) => {
  const response = await api.post('/api/grc/custom-frameworks', data);
  return response.data;
};

// ========== PDF Export ==========

export const downloadPdfReport = async (frameworkId = null) => {
  const params = frameworkId ? { framework_id: frameworkId } : {};
  const response = await api.get('/api/grc/reports/pdf', {
    params,
    responseType: 'blob'
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `compliance_report_${new Date().toISOString().slice(0,10)}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default {
  // Frameworks
  getFrameworks,
  getFramework,
  createFramework,
  getAvailableFrameworks,
  enableFramework,
  enableFrameworkWithControls,
  disableFramework,
  syncFrameworkControls,
  getTenantFrameworks,
  getCurrentTenantFrameworks,
  
  // Controls
  getControls,
  getControl,
  createControl,
  updateControl,
  deleteControl,
  
  // Documents
  uploadDocument,
  attachDocumentFile,
  getDocuments,
  getDocument,
  downloadDocument,
  approveDocument,
  updateDocument,
  deleteDocument,
  
  // Evidence
  uploadEvidence,
  attachEvidenceFile,
  getEvidence,
  getEvidenceById,
  downloadEvidence,
  approveEvidence,
  updateEvidence,
  deleteEvidence,
  
  // Policies
  approvePolicy,
  
  // Approval History
  getApprovalHistory,
  
  // Dashboard
  getExecutiveDashboard,
  getMappingReport
};

// ========== Link & Review APIs ==========

export const getLinkableItems = async (controlId, itemType, search = '') => {
  const params = { item_type: itemType };
  if (search) params.search = search;
  const response = await api.get(`/api/grc/controls/${controlId}/linkable`, { params });
  return response.data;
};

export const linkItemsToControl = async (controlId, data) => {
  const response = await api.post(`/api/grc/controls/${controlId}/link`, data);
  return response.data;
};

export const markDocumentReviewed = async (documentId, data = {}) => {
  const response = await api.post(`/api/grc/documents/${documentId}/review`, data);
  return response.data;
};

export const markEvidenceReviewed = async (evidenceId, data = {}) => {
  const response = await api.post(`/api/grc/evidence/${evidenceId}/review`, data);
  return response.data;
};

// ========== Policy Pack Generation ==========

export const getPackFrameworks = async () => {
  const response = await api.get('/api/grc/policies/generate-pack/frameworks');
  return response.data;
};

export const generatePolicyPack = async (data) => {
  const response = await api.post('/api/grc/policies/generate-pack', data);
  return response.data;
};

// ========== Notifications ==========

export const getNotifications = async (unreadOnly = false) => {
  const response = await api.get('/api/grc/notifications', { params: { unread_only: unreadOnly } });
  return response.data;
};

export const markNotificationRead = async (notifId) => {
  const response = await api.put(`/api/grc/notifications/${notifId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await api.put('/api/grc/notifications/mark-all-read');
  return response.data;
};

// ========== System Audit Trail ==========

export const getSystemAuditTrail = async (params = {}) => {
  const response = await api.get('/api/grc/system-audit-trail', { params });
  return response.data;
};

export const getAuditTrailStats = async (params = {}) => {
  const response = await api.get('/api/grc/system-audit-trail/stats', { params });
  return response.data;
};

export const getErrorLogSettings = async () => {
  const response = await api.get('/api/grc/system-audit-trail/error-settings');
  return response.data;
};

export const updateErrorLogSettings = async (payload) => {
  const response = await api.put('/api/grc/system-audit-trail/error-settings', payload);
  return response.data;
};
